import React from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  GridItem,
  HStack,
  Icon,
  Link,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Skeleton,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useToast,
  VStack,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  FiCreditCard,
  FiDownload,
  FiExternalLink,
  FiFileText,
  FiImage,
} from "react-icons/fi";
import { InvestmentPaymentCard } from "./common/InvestmentPaymentCard";
import { HistoryTimeline } from "./history/HistoryTimeline";
import { files } from "../services/api";

interface RecordDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  data: any;
  paymentMethod?: string | null;
  loadHistory?: (
    requestId: string,
    page: number,
    pageSize: number,
  ) => Promise<{
    history: any[];
    totalData: number;
    currentPage: number;
    totalPage: number;
  }>;
  onReconcileTadbirRequest?: (requestId: string) => Promise<void>;
  onRetryTadbirIssuanceRequest?: (requestId: string) => Promise<void>;
  onCheckSepahPayment?: (requestId: string) => Promise<void>;
  onSubmitSepahBuyOrder?: (requestId: string) => Promise<void>;
}

export const RecordDetailsModal: React.FC<RecordDetailsModalProps> = ({
  isOpen,
  onClose,
  title,
  data,
  paymentMethod,
  loadHistory,
  onReconcileTadbirRequest,
  onRetryTadbirIssuanceRequest,
  onCheckSepahPayment,
  onSubmitSepahBuyOrder,
}) => {
  const toast = useToast();
  const panelBg = useColorModeValue("light.700", "dark.700");
  const subtleBg = useColorModeValue("gray.50", "whiteAlpha.50");
  const borderColor = useColorModeValue("blackAlpha.100", "whiteAlpha.200");
  const labelColor = useColorModeValue("gray.500", "gray.400");
  const valueBg = useColorModeValue("white", "whiteAlpha.100");
  const resolvedPaymentMethod = paymentMethod || data?.paymentMethod || "";
  const summaryFields = React.useMemo(
    () => buildSummaryFields(data, resolvedPaymentMethod),
    [data, resolvedPaymentMethod],
  );
  const [historyState, setHistoryState] = React.useState<{
    items: any[];
    totalData: number;
    currentPage: number;
    totalPage: number;
    isLoading: boolean;
    isLoadingMore: boolean;
    error: string;
  }>({
    items: Array.isArray(data?.history) ? data.history : [],
    totalData: Number(data?.historyCount || 0),
    currentPage: 0,
    totalPage: 0,
    isLoading: false,
    isLoadingMore: false,
    error: "",
  });
  const receiptFile = data?.paymentSlip?.receiptFile;
  const shouldLoadSlipFile =
    isOpen &&
    resolvedPaymentMethod === "slip" &&
    Boolean(receiptFile?._id || receiptFile);
  const [slipFileState, setSlipFileState] = React.useState<{
    blobUrl: string;
    fileName: string;
    mimeType: string;
    isLoading: boolean;
  }>({
    blobUrl: "",
    fileName: "",
    mimeType: "",
    isLoading: false,
  });
  const [isReconciling, setIsReconciling] = React.useState(false);
  const [isRetryingIssuance, setIsRetryingIssuance] = React.useState(false);
  const [isCheckingSepahPayment, setIsCheckingSepahPayment] =
    React.useState(false);
  const [isSubmittingSepahBuyOrder, setIsSubmittingSepahBuyOrder] =
    React.useState(false);

  React.useEffect(() => {
    setHistoryState((current) => ({
      ...current,
      items: Array.isArray(data?.history) ? data.history : [],
      totalData: Number(
        data?.historyCount ||
          (Array.isArray(data?.history) ? data.history.length : 0),
      ),
      currentPage: 0,
      totalPage: 0,
      error: "",
    }));
  }, [data]);

  React.useEffect(() => {
    if (!isOpen || !loadHistory || !data?._id) {
      return;
    }

    let isMounted = true;

    const loadInitialHistory = async () => {
      try {
        setHistoryState((current) => ({
          ...current,
          isLoading: true,
          error: "",
        }));

        const result = await loadHistory(String(data._id), 1, 20);

        if (!isMounted) {
          return;
        }

        setHistoryState({
          items: Array.isArray(result.history) ? result.history : [],
          totalData: Number(result.totalData || 0),
          currentPage: Number(result.currentPage || 1),
          totalPage: Number(result.totalPage || 0),
          isLoading: false,
          isLoadingMore: false,
          error: "",
        });
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setHistoryState((current) => ({
          ...current,
          isLoading: false,
          isLoadingMore: false,
          error:
            error?.response?.data?.message ||
            error?.message ||
            "خطا در دریافت تاریخچه درخواست",
        }));
      }
    };

    void loadInitialHistory();

    return () => {
      isMounted = false;
    };
  }, [data?._id, isOpen, loadHistory]);

  const handleLoadMoreHistory = React.useCallback(async () => {
    if (
      !loadHistory ||
      !data?._id ||
      historyState.isLoading ||
      historyState.isLoadingMore ||
      historyState.currentPage >= historyState.totalPage
    ) {
      return;
    }

    try {
      setHistoryState((current) => ({
        ...current,
        isLoadingMore: true,
        error: "",
      }));

      const nextPage = historyState.currentPage + 1;
      const result = await loadHistory(String(data._id), nextPage, 20);

      setHistoryState((current) => ({
        ...current,
        items: [
          ...current.items,
          ...(Array.isArray(result.history) ? result.history : []),
        ],
        totalData: Number(result.totalData || current.totalData || 0),
        currentPage: Number(result.currentPage || nextPage),
        totalPage: Number(result.totalPage || current.totalPage || 0),
        isLoadingMore: false,
      }));
    } catch (error: any) {
      setHistoryState((current) => ({
        ...current,
        isLoadingMore: false,
        error:
          error?.response?.data?.message ||
          error?.message ||
          "خطا در دریافت ادامه تاریخچه",
      }));
    }
  }, [
    data?._id,
    historyState.currentPage,
    historyState.isLoading,
    historyState.isLoadingMore,
    historyState.totalPage,
    loadHistory,
  ]);

  React.useEffect(() => {
    if (!shouldLoadSlipFile) {
      setSlipFileState((current) => {
        if (current.blobUrl) {
          window.URL.revokeObjectURL(current.blobUrl);
        }

        return current.blobUrl || current.isLoading
          ? { blobUrl: "", fileName: "", mimeType: "", isLoading: false }
          : current;
      });
      return;
    }

    let isMounted = true;
    let nextBlobUrl = "";
    const currentFileId = String(receiptFile?._id || receiptFile || "").trim();

    const loadSlipFile = async () => {
      try {
        setSlipFileState((current) => ({ ...current, isLoading: true }));
        const response = await files.download(currentFileId);
        const mimeType =
          receiptFile?.type ||
          response.data?.type ||
          "application/octet-stream";
        const blob = new Blob([response.data], { type: mimeType });
        nextBlobUrl = window.URL.createObjectURL(blob);
        const contentDisposition = String(
          response.headers?.["content-disposition"] || "",
        );
        const fileName =
          extractFileName(contentDisposition) ||
          buildReceiptFileName(receiptFile, mimeType);

        if (!isMounted) {
          window.URL.revokeObjectURL(nextBlobUrl);
          return;
        }

        setSlipFileState({
          blobUrl: nextBlobUrl,
          fileName,
          mimeType,
          isLoading: false,
        });
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setSlipFileState({
          blobUrl: "",
          fileName: "",
          mimeType: "",
          isLoading: false,
        });
        toast({
          title: "خطا در دریافت فایل فیش",
          description:
            error?.response?.data?.message || error?.message || "خطای ناشناخته",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
      }
    };

    void loadSlipFile();

    return () => {
      isMounted = false;
      if (nextBlobUrl) {
        window.URL.revokeObjectURL(nextBlobUrl);
      }
    };
  }, [isOpen, receiptFile, resolvedPaymentMethod, shouldLoadSlipFile, toast]);

  const handleDownloadSlipFile = React.useCallback(() => {
    if (!slipFileState.blobUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = slipFileState.blobUrl;
    link.download = slipFileState.fileName || "payment-slip";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [slipFileState.blobUrl, slipFileState.fileName]);

  const paymentMethodBlock = renderPaymentMethodBlock(
    {
      data,
      paymentMethod: resolvedPaymentMethod,
      slipFileState,
      onDownloadSlipFile: handleDownloadSlipFile,
      colors: {
        labelColor,
        valueBg,
      },
    },
    {
      panelBg,
      subtleBg,
      borderColor,
    },
  );
  const historyItems = historyState.items;
  const normalizedExternalSource = String(data?.externalSource || "").trim();
  const canShowTadbirActions =
    Boolean(onReconcileTadbirRequest || onRetryTadbirIssuanceRequest) &&
    Boolean(data?._id) &&
    normalizedExternalSource === "tadbir";
  const canShowSepahActions =
    Boolean(onCheckSepahPayment || onSubmitSepahBuyOrder) &&
    Boolean(data?._id) &&
    normalizedExternalSource === "sepah_broker" &&
    ["wait_for_payment", "reviewing"].includes(String(data?.status || ""));
  const canRunCheckSepahPayment =
    data?.operationType === "issuance" &&
    data?.paymentMethod === "gateway" &&
    data?.status === "wait_for_payment";
  const canRunSubmitSepahBuyOrder =
    data?.operationType === "issuance" &&
    data?.paymentMethod === "gateway" &&
    data?.status === "reviewing";
  const canRunReconcileTadbir =
    normalizedExternalSource === "tadbir" &&
    String(data?.externalId || "").trim() !== "";
  const canRunRetryTadbirIssuance =
    data?.operationType === "issuance" &&
    data?.status === "reviewing" &&
    normalizedExternalSource === "tadbir" &&
    String(data?.externalId || "").trim() === "";
  const hasHistorySection =
    Boolean(loadHistory) ||
    historyState.isLoading ||
    historyState.isLoadingMore ||
    Boolean(historyState.error) ||
    historyItems.length > 0 ||
    Number(data?.historyCount || 0) > 0;

  const handleReconcileTadbir = React.useCallback(async () => {
    if (!onReconcileTadbirRequest || !data?._id) {
      return;
    }

    try {
      setIsReconciling(true);
      await onReconcileTadbirRequest(String(data._id));
    } catch (error: any) {
      toast({
        title: "خطا در همگام‌سازی تدبیر",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsReconciling(false);
    }
  }, [data?._id, onReconcileTadbirRequest, toast]);

  const handleRetryTadbirIssuance = React.useCallback(async () => {
    if (!onRetryTadbirIssuanceRequest || !data?._id) {
      return;
    }

    try {
      setIsRetryingIssuance(true);
      await onRetryTadbirIssuanceRequest(String(data._id));
    } catch (error: any) {
      toast({
        title: "خطا در ارسال مجدد صدور",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsRetryingIssuance(false);
    }
  }, [data?._id, onRetryTadbirIssuanceRequest, toast]);

  const handleCheckSepahPayment = React.useCallback(async () => {
    if (!onCheckSepahPayment || !data?._id) {
      return;
    }

    try {
      setIsCheckingSepahPayment(true);
      await onCheckSepahPayment(String(data._id));
    } catch (error: any) {
      toast({
        title: "خطا در بررسی پرداخت سپه",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCheckingSepahPayment(false);
    }
  }, [data?._id, onCheckSepahPayment, toast]);

  const handleSubmitSepahBuyOrder = React.useCallback(async () => {
    if (!onSubmitSepahBuyOrder || !data?._id) {
      return;
    }

    try {
      setIsSubmittingSepahBuyOrder(true);
      await onSubmitSepahBuyOrder(String(data._id));
    } catch (error: any) {
      toast({
        title: "خطا در ثبت سفارش خرید سپه",
        description:
          error?.response?.data?.message || error?.message || "خطای ناشناخته",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmittingSepahBuyOrder(false);
    }
  }, [data?._id, onSubmitSepahBuyOrder, toast]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{title}</ModalHeader>
        <ModalCloseButton left={4} right="auto" aria-label="بستن" />
        <ModalBody pb={6}>
          {hasHistorySection ? (
            <Tabs isFitted>
              <TabList mb={4}>
                <Tab>جزئیات درخواست</Tab>
                <Tab>
                  <HStack spacing={2}>
                    <Text>تاریخچه</Text>
                    <Badge colorScheme="brand" variant="subtle">
                      {Number(
                        historyState.totalData || historyItems.length,
                      ).toLocaleString("fa-IR")}
                    </Badge>
                  </HStack>
                </Tab>
              </TabList>

              <TabPanels>
                <TabPanel px={0}>
                  <VStack align="stretch" spacing={4}>
                    {canShowSepahActions ? (
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="2xl"
                        p={4}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Text fontWeight="bold">اقدامات سپه</Text>
                          <Text fontSize="sm" color={labelColor}>
                            برای همین درخواست می‌توانید وضعیت پرداخت سپه را
                            دوباره بررسی و ثبت خرید را پیگیری کنید.
                          </Text>
                          <Button
                            onClick={handleCheckSepahPayment}
                            isLoading={isCheckingSepahPayment}
                            colorScheme="brand"
                            isDisabled={!canRunCheckSepahPayment}
                          >
                            اجرای بررسی پرداخت سپه
                          </Button>
                          <Button
                            onClick={handleSubmitSepahBuyOrder}
                            isLoading={isSubmittingSepahBuyOrder}
                            colorScheme="blue"
                            isDisabled={!canRunSubmitSepahBuyOrder}
                          >
                            ثبت سفارش خرید سپه
                          </Button>
                        </VStack>
                      </Box>
                    ) : null}
                    {canShowTadbirActions ? (
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="2xl"
                        p={4}
                      >
                        <VStack align="stretch" spacing={3}>
                          <Text fontWeight="bold">اقدامات تدبیر</Text>
                          <Text fontSize="sm" color={labelColor}>
                            برای همین درخواست، همگام‌سازی وضعیت یا تلاش مجدد
                            ارسال صدور را به صورت دستی اجرا کنید.
                          </Text>
                          <HStack
                            spacing={3}
                            align="stretch"
                            flexDirection={{ base: "column", md: "row" }}
                          >
                            {onReconcileTadbirRequest ? (
                              <Button
                                onClick={handleReconcileTadbir}
                                isLoading={isReconciling}
                                variant="outline"
                                flex="1"
                                isDisabled={!canRunReconcileTadbir}
                              >
                                اجرای همگام‌سازی تدبیر
                              </Button>
                            ) : null}
                            {onRetryTadbirIssuanceRequest ? (
                              <Button
                                onClick={handleRetryTadbirIssuance}
                                isLoading={isRetryingIssuance}
                                colorScheme="brand"
                                flex="1"
                                isDisabled={!canRunRetryTadbirIssuance}
                              >
                                اجرای ارسال مجدد صدور
                              </Button>
                            ) : null}
                          </HStack>
                          <Text fontSize="xs" color={labelColor}>
                            همگام‌سازی فقط برای درخواست‌های دارای شناسه تدبیر
                            فعال است. ارسال مجدد صدور فقط برای درخواست‌های صدور
                            در حال بررسی که هنوز شناسه تدبیر ندارند فعال می‌شود.
                          </Text>
                        </VStack>
                      </Box>
                    ) : null}
                    {summaryFields.length ? (
                      <Box
                        borderWidth="1px"
                        borderColor={borderColor}
                        borderRadius="2xl"
                        bg={panelBg}
                        p={4}
                      >
                        <VStack align="stretch" spacing={4}>
                          <Text fontWeight="bold">خلاصه درخواست</Text>
                          <Grid
                            templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                            gap={4}
                          >
                            {summaryFields.map((field) => (
                              <GridItem key={field.label}>
                                <Text fontSize="xs" color={labelColor} mb={1}>
                                  {field.label}
                                </Text>
                                <Box
                                  bg={valueBg}
                                  borderWidth="1px"
                                  borderColor={borderColor}
                                  borderRadius="xl"
                                  px={3}
                                  py={2}
                                >
                                  <Text fontWeight="semibold">
                                    {field.value}
                                  </Text>
                                </Box>
                              </GridItem>
                            ))}
                          </Grid>
                        </VStack>
                      </Box>
                    ) : null}
                    {paymentMethodBlock ? <>{paymentMethodBlock}</> : null}
                  </VStack>
                </TabPanel>

                <TabPanel px={0}>
                  <HistoryTimeline
                    title="تاریخچه تغییرات درخواست"
                    emptyMessage="هنوز رویدادی برای این درخواست ثبت نشده است."
                    state={historyState}
                    onLoadMore={handleLoadMoreHistory}
                    eventLabelMap={historyEventMeta}
                    sourceLabelMap={historySourceMeta}
                    fieldLabelMap={historyFieldMeta}
                    formatValue={formatHistoryValue}
                  />
                </TabPanel>
              </TabPanels>
            </Tabs>
          ) : (
            <VStack align="stretch" spacing={4}>
              {canShowSepahActions ? (
                <Box
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="2xl"
                  p={4}
                >
                  <VStack align="stretch" spacing={3}>
                    <Text fontWeight="bold">اقدامات سپه</Text>
                    <Text fontSize="sm" color={labelColor}>
                      برای همین درخواست می‌توانید وضعیت پرداخت سپه را دوباره
                      بررسی و ثبت خرید را پیگیری کنید.
                    </Text>
                    <Button
                      onClick={handleCheckSepahPayment}
                      isLoading={isCheckingSepahPayment}
                      colorScheme="brand"
                    >
                      اجرای بررسی پرداخت سپه
                    </Button>
                  </VStack>
                </Box>
              ) : null}
              {canShowTadbirActions ? (
                <Box
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="2xl"
                  p={4}
                >
                  <VStack align="stretch" spacing={3}>
                    <Text fontWeight="bold">اقدامات تدبیر</Text>
                    <Text fontSize="sm" color={labelColor}>
                      برای همین درخواست، همگام‌سازی وضعیت یا تلاش مجدد ارسال
                      صدور را به صورت دستی اجرا کنید.
                    </Text>
                    <HStack
                      spacing={3}
                      align="stretch"
                      flexDirection={{ base: "column", md: "row" }}
                    >
                      {onReconcileTadbirRequest ? (
                        <Button
                          onClick={handleReconcileTadbir}
                          isLoading={isReconciling}
                          variant="outline"
                          flex="1"
                          isDisabled={!canRunReconcileTadbir}
                        >
                          اجرای همگام‌سازی تدبیر
                        </Button>
                      ) : null}
                      {onRetryTadbirIssuanceRequest ? (
                        <Button
                          onClick={handleRetryTadbirIssuance}
                          isLoading={isRetryingIssuance}
                          colorScheme="brand"
                          flex="1"
                          isDisabled={!canRunRetryTadbirIssuance}
                        >
                          اجرای ارسال مجدد صدور
                        </Button>
                      ) : null}
                    </HStack>
                    <Text fontSize="xs" color={labelColor}>
                      همگام‌سازی فقط برای درخواست‌های دارای شناسه تدبیر فعال
                      است. ارسال مجدد صدور فقط برای درخواست‌های صدور در حال
                      بررسی که هنوز شناسه تدبیر ندارند فعال می‌شود.
                    </Text>
                  </VStack>
                </Box>
              ) : null}
              {summaryFields.length ? (
                <Box
                  borderWidth="1px"
                  borderColor={borderColor}
                  borderRadius="2xl"
                  bg={panelBg}
                  p={4}
                >
                  <VStack align="stretch" spacing={4}>
                    <Text fontWeight="bold">خلاصه درخواست</Text>
                    <Grid
                      templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                      gap={4}
                    >
                      {summaryFields.map((field) => (
                        <GridItem key={field.label}>
                          <Text fontSize="xs" color={labelColor} mb={1}>
                            {field.label}
                          </Text>
                          <Box
                            bg={valueBg}
                            borderWidth="1px"
                            borderColor={borderColor}
                            borderRadius="xl"
                            px={3}
                            py={2}
                          >
                            <Text fontWeight="semibold">{field.value}</Text>
                          </Box>
                        </GridItem>
                      ))}
                    </Grid>
                  </VStack>
                </Box>
              ) : null}
              {paymentMethodBlock ? <>{paymentMethodBlock}</> : null}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

const renderPaymentMethodBlock = (
  {
    data,
    paymentMethod,
    slipFileState,
    onDownloadSlipFile,
  }: {
    data: any;
    paymentMethod: string;
    slipFileState: {
      blobUrl: string;
      fileName: string;
      mimeType: string;
      isLoading: boolean;
    };
    onDownloadSlipFile: () => void;
    colors: {
      labelColor: string;
      valueBg: string;
    };
  },
  {
    borderColor,
  }: {
    panelBg: string;
    subtleBg: string;
    borderColor: string;
  },
) => {
  if (!paymentMethod) {
    return null;
  }

  if (paymentMethod === "card" && data.paymentCard) {
    const heroBg =
      "linear-gradient(135deg, rgba(38,132,255,0.14), rgba(38,132,255,0.14))";

    return (
      <VStack align="stretch" spacing={4}>
        <Box
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="2xl"
          p={4}
          bg={heroBg}
        >
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={2}>
                <Icon as={FiFileText as any} boxSize={6} />
                <Text fontWeight="bold">پرداخت با کارت هدیه</Text>
              </HStack>
            </HStack>
            <Text fontSize="sm">{formatInvestorLabel(data.investor)}</Text>
          </VStack>
        </Box>
        <InvestmentPaymentCard
          cardNumber={data.paymentCard?.cardNumber}
          amount={data.paymentCard?.amount || data.amountRial}
          expiresInfo={{
            label: "انقضا",
            value: data.paymentCard?.expiresAt || data.createdAt,
          }}
          status={data.paymentCard?.status || data.status}
          usedAt={data.paymentCard?.usedAt}
          cancelledAt={
            data.paymentCard?.cancelledAt ||
            data.paymentCard?.batchId?.cancelledAt
          }
          fundName={data.fundName || "صندوق سرمایه‌گذاری"}
        />
      </VStack>
    );
  }

  if (paymentMethod === "gateway") {
    const heroBg =
      "linear-gradient(135deg, rgba(38,132,255,0.14), rgba(38,132,255,0.14))";

    return (
      <VStack align="stretch" spacing={4}>
        <Box
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="2xl"
          p={4}
          bg={heroBg}
        >
          <VStack align="stretch" spacing={3}>
            <HStack justify="space-between" align="flex-start">
              <HStack spacing={2}>
                <Icon as={FiCreditCard as any} boxSize={5} />
                <Text fontWeight="bold">پرداخت از طریق درگاه</Text>
              </HStack>
            </HStack>
            <Text fontSize="sm">{formatInvestorLabel(data.investor)}</Text>
          </VStack>
        </Box>
      </VStack>
    );
  }

  if (paymentMethod === "slip" && data.paymentSlip) {
    const canPreviewInline = isPreviewableInline(slipFileState.mimeType);
    const isImagePreview = slipFileState.mimeType.startsWith("image/");

    return (
      <Box borderWidth="1px" borderColor={borderColor} borderRadius="2xl" p={4}>
        <VStack align="stretch" spacing={4}>
          <Box
            borderWidth="1px"
            borderColor={borderColor}
            borderRadius="2xl"
            p={4}
            bg="linear-gradient(135deg, rgba(38,132,255,0.14), rgba(38,132,255,0.14))"
          >
            <VStack align="stretch" spacing={3}>
              <HStack justify="space-between" align="flex-start">
                <HStack spacing={2}>
                  <Icon as={FiFileText as any} boxSize={6} />
                  <Text fontWeight="bold">فیش انتقال بانکی</Text>
                </HStack>
              </HStack>
              <Text fontSize="sm">{formatInvestorLabel(data.investor)}</Text>
            </VStack>
          </Box>
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={4}>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                مبلغ
              </Text>
              <Text fontWeight="semibold">
                {formatAmount(data.paymentSlip?.amountRial || data.amountRial)}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                شماره حساب مبدا
              </Text>
              <Text fontWeight="semibold">
                {data.paymentSlip?.sourceAccountNumber || "—"}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                شماره حساب مقصد
              </Text>
              <Text fontWeight="semibold">
                {data.paymentSlip?.destinationAccountNumber || "—"}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                تاریخ
              </Text>
              <Text fontWeight="semibold">
                {formatDateTime(
                  data.paymentSlip?.transferedAt ||
                    data.updatedAt ||
                    data.createdAt,
                )}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                بانک مبدا
              </Text>
              <Text fontWeight="semibold">
                {data.paymentSlip?.sourceBankName || "—"}
              </Text>
            </GridItem>
            <GridItem>
              <Text fontSize="xs" color="gray.500" mb={1}>
                کد بانک مبدا
              </Text>
              <Text fontWeight="semibold">
                {data.paymentSlip?.sourceBankCode || "—"}
              </Text>
            </GridItem>
          </Grid>
          <Divider />
          <HStack spacing={2} my={2} w="100%" justify="center" align="center">
            <HStack spacing={2} w="100%">
              <Icon as={FiImage as any} boxSize={6} />
              <Text fontWeight="medium">رسید ثبت‌شده</Text>
            </HStack>
            <HStack spacing={3}>
              <Button
                size="sm"
                leftIcon={<Icon as={FiDownload as any} />}
                colorScheme="brand"
                onClick={onDownloadSlipFile}
                isDisabled={!slipFileState.blobUrl}
              >
                دانلود فایل
              </Button>
              <Button
                as={Link}
                size="sm"
                leftIcon={<Icon as={FiExternalLink as any} />}
                variant="outline"
                href={slipFileState.blobUrl || undefined}
                target="_blank"
                rel="noopener noreferrer"
                isDisabled={!slipFileState.blobUrl}
              >
                مشاهده
              </Button>
            </HStack>
          </HStack>
          <VStack align="stretch" spacing={3}>
            {slipFileState.isLoading ? (
              <Skeleton borderRadius="lg" height="320px" />
            ) : null}
            {!slipFileState.isLoading &&
            canPreviewInline &&
            slipFileState.blobUrl ? (
              <Box
                overflow="hidden"
                borderRadius="lg"
                borderWidth="1px"
                borderColor={borderColor}
                bg="blackAlpha.50"
              >
                {isImagePreview ? (
                  <Image
                    src={slipFileState.blobUrl}
                    alt="رسید فیش انتقال"
                    w="100%"
                    maxH="420px"
                    objectFit="contain"
                  />
                ) : (
                  <Box
                    as="iframe"
                    src={slipFileState.blobUrl}
                    title="پیش‌نمایش فیش انتقال"
                    w="100%"
                    h="420px"
                  />
                )}
              </Box>
            ) : null}
            {!slipFileState.isLoading &&
            !canPreviewInline &&
            slipFileState.blobUrl ? (
              <Text fontSize="sm" color="gray.500">
                پیش‌نمایش مستقیم برای این نوع فایل در دسترس نیست. فایل را دانلود
                یا در پنجره جدید باز کنید.
              </Text>
            ) : null}
          </VStack>
        </VStack>
      </Box>
    );
  }

  return null;
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "—";

  const formatter = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = Object.fromEntries(
    formatter
      .formatToParts(parsedDate)
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, part.value]),
  );

  return `${parts.year}/${parts.month}/${parts.day} - ${parts.hour}:${parts.minute}`;
};

const formatAmount = (value?: number | null) =>
  value ? `${Number(value).toLocaleString("fa-IR")} ریال` : "—";

const formatNumber = (value?: number | null) =>
  value || value === 0 ? Number(value).toLocaleString("fa-IR") : "—";

const buildSummaryFields = (data: any, paymentMethod?: string) => {
  if (!data) {
    return [];
  }

  const baseFields = [
    {
      label: "سرمایه‌گذار",
      value: formatInvestorLabel(data.investor),
    },
    {
      label: "صندوق",
      value: data.fundName || data.fundSeoRegisterNo || "—",
    },
    {
      label: "شماره رهگیری",
      value: data.trackId || data.referenceNumber || data.orderId || "—",
    },
    {
      label: "روش پرداخت",
      value: paymentMethodMeta[paymentMethod || ""] || "—",
    },
    {
      label: "وضعیت",
      value: statusMeta[data.status || ""] || data.status || "—",
    },
    {
      label: "مبلغ",
      value:
        data.amountRial || data.amountRial === 0
          ? formatAmount(data.amountRial)
          : "—",
    },
  ];

  const hasAnyPrimaryValue = baseFields.some(
    (field) => field.value && field.value !== "—",
  );

  if (paymentMethod || hasAnyPrimaryValue) {
    return baseFields;
  }

  return Object.entries(data)
    .filter(([, value]) =>
      value === 0 || value
        ? ["string", "number", "boolean"].includes(typeof value)
        : false,
    )
    .slice(0, 8)
    .map(([key, value]) => ({
      label: key,
      value:
        typeof value === "boolean" ? (value ? "بله" : "خیر") : String(value),
    }));
};

const formatInvestorLabel = (investor?: any) => {
  const fullName = String(investor?.fullName || "").trim();
  const nationalId = String(investor?.nationalId || "").trim();

  if (fullName && nationalId) {
    return `${fullName} - ${nationalId}`;
  }

  return fullName || nationalId || "—";
};

const paymentMethodMeta: Record<string, string> = {
  gateway: "درگاه پرداخت",
  card: "کارت هدیه",
  slip: "فیش انتقال",
};

const statusMeta: Record<string, string> = {
  wait_for_payment: "در انتظار پرداخت",
  reviewing: "در حال بررسی",
  approved: "تایید شده",
  rejected: "رد شده",
  failed: "ناموفق",
};

const operationTypeMeta: Record<string, string> = {
  issuance: "صدور",
  redemption: "ابطال",
};

const historyEventMeta: Record<string, string> = {
  created: "ایجاد درخواست",
  updated: "ویرایش درخواست",
  status_changed: "تغییر وضعیت",
  payment_slip_attached: "ثبت فیش انتقال",
  payment_submitted: "ارسال فرآیند پرداخت",
  payment_confirmed: "پرداخت موفق",
  payment_failed: "پرداخت ناموفق",
  payment_cancelled: "لغو پرداخت",
  sepah_submission_succeeded: "ثبت و ارسال موفق به سپه",
  sepah_submission_failed: "خطا در ثبت سفارش خرید سپه",
  transaction_linked: "اتصال تراکنش",
  tadbir_submission_succeeded: "ثبت و ارسال موفق به تدبیر",
  tadbir_submission_failed: "خطا در ارسال به تدبیر",
  tadbir_status_synced: "همگام‌سازی وضعیت تدبیر",
  deleted: "حذف درخواست",
};

const historySourceMeta: Record<string, string> = {
  request_controller: "کاربر سامانه",
  payment_slip: "فیش انتقال",
  payment_gateway: "درگاه پرداخت",
  payment_card: "کارت هدیه",
  tadbir: "تدبیر",
  sepah: "سپه",
  job: "پردازش خودکار",
};

const historyFieldMeta: Record<string, string> = {
  status: "وضعیت",
  paymentMethod: "روش پرداخت",
  operationType: "نوع عملیات",
  amountRial: "مبلغ",
  units: "تعداد واحد",
  unitPrice: "قیمت هر واحد",
  fundName: "نام صندوق",
  fundSeoRegisterNo: "شناسه صندوق",
  paymentSlip: "فیش انتقال",
  paymentCard: "کارت پرداخت",
  transaction: "تراکنش",
  externalStatus: "وضعیت تدبیر",
  externalId: "شناسه خارجی",
  externalSource: "منبع خارجی",
  externalLastSyncedAt: "آخرین همگام‌سازی",
  trackId: "شماره رهگیری",
};

const formatHistoryValue = (field: string, value: any) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  if (field === "status") {
    return statusMeta[String(value)] || String(value);
  }

  if (field === "paymentMethod") {
    return paymentMethodMeta[String(value)] || String(value);
  }

  if (field === "operationType") {
    return operationTypeMeta[String(value)] || String(value);
  }

  if (field === "amountRial") {
    return formatAmount(Number(value));
  }

  if (field === "units") {
    return formatNumber(Number(value) / 10000);
  }

  if (field === "unitPrice") {
    return formatAmount(Number(value));
  }

  if (
    field.toLowerCase().includes("at") ||
    field.toLowerCase().includes("date")
  ) {
    return formatDateTime(value);
  }

  if (typeof value === "boolean") {
    return value ? "بله" : "خیر";
  }

  if (Array.isArray(value)) {
    return value.join("، ");
  }

  if (typeof value === "object") {
    if (value?.fullName || value?.nationalId) {
      return formatInvestorLabel(value);
    }

    if (typeof value?._id === "string") {
      return value._id;
    }

    try {
      return JSON.stringify(value);
    } catch {
      return "—";
    }
  }

  return String(value);
};

const extractFileName = (contentDisposition?: string) => {
  const encodedMatch = contentDisposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1]);
    } catch {
      return encodedMatch[1];
    }
  }

  const plainMatch = contentDisposition?.match(/filename="?([^"]+)"?/i);
  return plainMatch?.[1] || "";
};

const buildReceiptFileName = (receiptFile?: any, mimeType?: string) => {
  const extension =
    String(receiptFile?.extension || "").trim() ||
    mimeTypeToExtension(mimeType);
  return extension ? `payment-slip.${extension}` : "payment-slip";
};

const mimeTypeToExtension = (mimeType?: string) => {
  const normalized = String(mimeType || "").toLowerCase();

  if (normalized === "application/pdf") return "pdf";
  if (normalized === "image/jpeg") return "jpg";
  if (normalized === "image/png") return "png";
  if (normalized === "image/webp") return "webp";

  return "";
};

const isPreviewableInline = (mimeType?: string) => {
  const normalized = String(mimeType || "").toLowerCase();
  return normalized.startsWith("image/") || normalized === "application/pdf";
};
